/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Dialog, Radio, RadioGroup, breakpoints } from '@coursera/cds-core';

import { getAdditionalFilterOptions } from 'bundles/enterprise-admin-user-filters/constants';
import type { AdditionalAttributeFilter, AdditionalFilterType } from 'bundles/enterprise-admin-user-filters/types';
import { Spacer } from 'bundles/enterprise-ui/components';

import _t from 'i18n!nls/enterprise-admin-user-filters';

const { HeadingGroup, Content, Actions } = Dialog;

const AdditionalFilterDialog = ({
  open,
  otherFilters,
  onClose,
  onSelect,
  ...rest
}: {
  open: boolean;
  otherFilters: AdditionalAttributeFilter[];
  onClose: () => void;
  onSelect: (filter: AdditionalFilterType) => void;
}) => {
  const [filter, setFilter] = React.useState<AdditionalFilterType | undefined>();
  const options = getAdditionalFilterOptions();
  const onChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setFilter(event.target.value as AdditionalFilterType);
  const hideContract = otherFilters.some((_) => _.attributeName === 'CONTRACT');
  const elpOptions = options.filter(
    (_) => _.value !== 'CONTRACT' && otherFilters.every((other) => other.attributeName !== _.value)
  );
  return (
    <Dialog
      aria-describedby="additional-filter-dialog-content"
      open={open}
      variant="standard"
      onClose={onClose}
      {...rest}
    >
      <HeadingGroup>{_t('Select an additional filter to add')}</HeadingGroup>
      <Content id="additional-filter-dialog-content">
        <div css={{ padding: 24, background: 'var(--cds-color-grey-25)' }}>
          {!hideContract && (
            <React.Fragment>
              <RadioGroup value={filter} label={_t('Select the available filter to add')} onChange={onChange}>
                {[
                  <Radio
                    key="CONTRACT"
                    label={_t('Active contract')}
                    supportText={_t('Filter programs by active contracts')}
                    value="CONTRACT"
                  />,
                ]}
              </RadioGroup>
              <Spacer size={16} />
            </React.Fragment>
          )}
          {elpOptions.length > 0 && (
            <RadioGroup value={filter} label={_t('Learner attributes')} onChange={onChange}>
              {elpOptions.map((props) => (
                <Radio key={props.value} {...props} />
              ))}
            </RadioGroup>
          )}
        </div>
      </Content>
      <Actions>
        <Button
          css={css`
            ${breakpoints.down('xs')} {
              width: 100%;
              max-width: none;
            }
          `}
          size="medium"
          variant="primary"
          disabled={!filter}
          onClick={() => filter && onSelect(filter)}
          data-testid="select-additional-filter"
        >
          {_t('Select')}
        </Button>
        <Button
          css={css`
            margin: 0 var(--cds-spacing-300) 0;
            ${breakpoints.down('xs')} {
              width: 100%;
              max-width: none;
              margin: var(--cds-spacing-300) 0 0;
            }
          `}
          size="medium"
          variant="secondary"
          onClick={onClose}
          data-testid="close-additional-filter-dialog"
        >
          {_t('Cancel')}
        </Button>
      </Actions>
    </Dialog>
  );
};
export default AdditionalFilterDialog;
